<template>
  <div class="email-form">
    <h1>Forgot Your Password?</h1>

    <!-- eslint-disable-next-line -->
    <p>
      Please enter the email address for your account and submit to receive an email with instructions to reset your
      password
    </p>

    <div class="email-input">
      <FormInput
        type="email"
        label="Enter Your Email Here"
        name="email"
        v-model.trim="v$.emailField.$model"
        :valid="!v$.emailField.$error"
      />
    </div>

    <p class="errorText">
      {{ error }}
    </p>

    <button type="button" :class="{ 'submit-button': true, disabled: disabled }" @click="onSubmit">Submit</button>
    <router-link class="back-link" :to="{ name: 'login' }">Back</router-link>
  </div>
</template>

<script setup>
import { useVuelidate } from '@vuelidate/core';
import { required, email } from '@vuelidate/validators';
import { ref, computed, watch } from 'vue';

import FormInput from '@/components/form/elements/FormInput.vue';

const emit = defineEmits(['send']);
const props = defineProps({
  error: {
    type: String,
    default: '',
  },
});

const emailField = ref('');
const submitting = ref(false);

const rules = computed(() => ({
  emailField: {
    required,
    email,
  },
}));

const v$ = useVuelidate(rules, { emailField });

const disabled = computed(() => {
  return !emailField.value || v$.$invalid || submitting.value;
});

function onSubmit() {
  if (disabled.value) {
    return;
  }

  submitting.value = true;
  emit('send', emailField.value);
}

watch(
  () => props.error,
  (val) => {
    if (val) {
      submitting.value = false;
    }
  }
);
</script>

<style scoped>
.email-form {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  max-width: 45em;
  width: 100%;
  font-size: 1.1em;
  text-align: center;
}

h1,
p,
a {
  margin: 0;
  padding: 0;
  color: #fff;
  font-weight: 400;
}

p {
  margin: 1em 0;
}

/**
 * Custom form input
 */
.email-input {
  max-width: 25em;
  width: 100%;
}
.email-input :deep(.form-input) {
  display: flex;
  justify-content: center;
}
/** input label */
.email-input :deep(.form-input).focused label,
.email-input :deep(.form-input).filled label {
  bottom: 0.2em;
  font-size: 1.1em;
  opacity: 0;
}
.email-input :deep(.form-input) label {
  color: #fff;
}
/** input box */
.email-input :deep(.form-input) input {
  border-bottom: 1px solid #fff;
  color: #fff;
  text-align: center;
}
.email-input :deep(.form-input):not(.focused).filled input {
  color: #fff;
}
.email-input :deep(.form-input):not(.focused).error input {
  color: #ffc4c9;
}
/**
 * End Custom form input
 */

.errorText {
  margin: 0;
  height: 4em;
  padding: 0.5em 0 0;
  color: #ffc4c9;
}

.submit-button {
  display: flex;
  align-items: center;
  justify-content: center;

  border-radius: 0.5em;
  padding: 0.5em 6em;
  background: #fff;
  color: #808285;
  font-size: 1em;
  cursor: pointer;
}

.submit-button.disabled {
  cursor: default;
  pointer-events: none;
}

.back-link {
  margin-top: 0.5em;
  text-decoration: none;
}
</style>
